import { useEffect, useState } from 'react';
import { customerApi } from '../customer_api';
import { formatDate, formatMoney } from '../lib/format';

/**
 * Subset of the API AccountCredit shape consumed by the customer-side
 * Credits page. Loose-typed `status` so a future status doesn't break the
 * page. Mirrors apps/api/src/db/schema/account_credits.ts.
 */
export interface CustomerAccountCredit {
  id: string;
  accountId: string;
  customerId: string;
  amountMinor: number;
  currency: string;
  remainingMinor: number;
  reason: string;
  issuedByMembershipId: string | null;
  expiresAt: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Render the time-to-expiry copy. Format aims to be human:
 *   - within a week: "in N days" (or "today" / "tomorrow")
 *   - more than a week: "expires on Mon, 1 Jul"
 *   - no expiry: "never expires"
 */
function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'never expires';
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return expiresAt;
  const diffMs = expiry.getTime() - Date.now();
  if (diffMs <= 0) return 'expired';
  const diffDays = Math.floor(diffMs / DAY_MS);
  if (diffDays === 0) return 'expires today';
  if (diffDays === 1) return 'expires tomorrow';
  if (diffDays < 7) return `expires in ${diffDays} days`;
  return `expires ${formatDate(expiresAt)}`;
}

export function Credits() {
  const [credits, setCredits] = useState<CustomerAccountCredit[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    customerApi<{ credits: CustomerAccountCredit[] }>('/api/customer/credits')
      .then((data) => setCredits(data.credits))
      .catch(() => setError('Could not load credits. Please try again.'));
  }, []);

  return (
    <main className="min-h-[100dvh] bg-background px-4 py-6 sm:px-6 sm:py-10">
      <div className="mx-auto max-w-2xl">
        <span
          style={{
            fontSize: '0.7rem',
            textTransform: 'uppercase',
            letterSpacing: '0.18em',
            color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
          }}
        >
          Account
        </span>
        <h1
          style={{
            fontSize: '2.25rem',
            fontWeight: 500,
            letterSpacing: '-0.02em',
            margin: '0.4rem 0 1.5rem',
          }}
        >
          Credits
        </h1>

        {error && <p style={{ color: 'var(--destructive, oklch(0.55 0.15 30))' }}>{error}</p>}

        {!error && credits === null && (
          <p style={{ color: 'var(--muted-foreground, oklch(0.52 0.008 75))' }}>Loading…</p>
        )}

        {credits !== null && credits.length === 0 && (
          <div
            style={{
              marginTop: '2rem',
              padding: '2.5rem 1.5rem',
              borderRadius: '14px',
              border: '1px solid var(--border, oklch(0.915 0.005 80))',
              textAlign: 'center',
            }}
          >
            <p
              style={{
                margin: 0,
                color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                fontSize: '0.95rem',
              }}
            >
              No credits on your account.
            </p>
          </div>
        )}

        {credits !== null && credits.length > 0 && (
          <div style={{ borderTop: '1px solid var(--border, oklch(0.915 0.005 80))' }}>
            {credits.map((c) => (
              <div
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  padding: '0.9rem 0',
                  borderBottom: '1px solid var(--border, oklch(0.915 0.005 80))',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: 500 }}>{c.reason}</div>
                  <div
                    style={{
                      fontSize: '0.78rem',
                      color: 'var(--muted-foreground, oklch(0.52 0.008 75))',
                      marginTop: '0.15rem',
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                    }}
                  >
                    <span className="num">Issued {formatDate(c.createdAt)}</span>
                    <span aria-hidden="true">·</span>
                    <span>{formatExpiry(c.expiresAt)}</span>
                  </div>
                </div>
                <span
                  className="num"
                  style={{
                    fontWeight: 500,
                    color: 'oklch(0.40 0.12 150)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatMoney(c.remainingMinor, c.currency)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
